import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SHOP_URL, scrollToId } from "../../lib/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-champagne/80 backdrop-blur-xl border-b border-espresso/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 md:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
        >
          <img
            src={scrolled ? "/logo-plum.png" : "/logo-gold.png"}
            alt="Cibele Belly Dance"
            className={`h-11 w-auto transition-all duration-300 md:h-14 ${
              scrolled ? "" : "drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
            }`}
          />
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className={`group relative font-sans text-[0.7rem] uppercase tracking-[0.28em] transition-colors duration-300 hover:text-gold ${
                scrolled ? "text-espresso/80" : "text-champagne/90"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-400 group-hover:w-full" />
            </button>
          ))}
          <a
            data-testid="nav-shop-btn"
            href={SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded-full px-6 py-2.5 font-sans text-[0.7rem] uppercase tracking-[0.24em] transition-colors duration-300 ${
              scrolled
                ? "bg-plum text-champagne hover:bg-espresso"
                : "bg-peach text-espresso hover:bg-champagne"
            }`}
          >
            Shop
          </a>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <a
            data-testid="mobile-nav-shop-btn"
            href={SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded-full px-4 py-2 font-sans text-[0.65rem] uppercase tracking-[0.2em] transition-colors duration-300 ${
              scrolled
                ? "bg-plum text-champagne hover:bg-espresso"
                : "bg-peach text-espresso hover:bg-champagne"
            }`}
          >
            Shop
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`transition-colors duration-300 ${
              scrolled ? "text-espresso" : "text-champagne"
            }`}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden border-t border-espresso/10 bg-champagne/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-6 py-6">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="border-b border-espresso/10 py-4 text-left font-serif text-2xl text-espresso"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="mobile-menu-shop-btn"
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-6 inline-flex w-fit rounded-full bg-plum px-8 py-3 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-champagne transition-colors duration-300 hover:bg-espresso"
              >
                Shop
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
